import { FormikProps, useFormikContext } from "formik";
import { useContext, useEffect, useMemo, useState } from "react";
import { FormattedMessage } from 'react-intl';
import { CountryCodes, FieldType, SectionType } from "../components/types";
import { checkIfIsCountryField, checkIfIsStateField, getPostalCodeCountryByState, isPostalCodeValid } from "../helpers";
import { CommonDataContext } from "../context";
import _ from "lodash";

export default function usePostalcode(field: FieldType, section: SectionType, value: string) {

  const formik: FormikProps<any> = useFormikContext();

  const { highlightErrorFieldsState } = useContext(CommonDataContext);

  const [isInvalid, setIsInvalid] = useState(false);
  const [postalCodeCountry, setPostalCodeCountry] = useState<CountryCodes>(CountryCodes.US);

  const checkIfInvalid = (value: string): boolean => {
    const isRequiredAndEmpty = !!field.config.required && !value;
    return isRequiredAndEmpty || !isPostalCodeValid(value, postalCodeCountry);
  };

  const HelperText = useMemo(() => (
    postalCodeCountry === CountryCodes.US
      ? <FormattedMessage
        id={'incorrectPostalCodeUSFormat'}
        defaultMessage={'incorrectPostalCodeUSFormat'}
      />
      : <FormattedMessage
        id={'incorrectPostalCodeCanadianFormat'}
        defaultMessage={'incorrectPostalCodeCanadianFormat'}
      />
  ), [postalCodeCountry]);

  const checkAndSetIsInvalid = (value: string) => {
    setIsInvalid(checkIfInvalid(value));
  };

  useEffect(() => {
    const sectionFields = section?.fields ?? [];
    const currentFieldIndex = sectionFields.findIndex((f: FieldType) => f.id === field.id);

    // the related field is expected to precede the postal code, so look backwards from the current field;
    // fall back to the first matching field in the section if there is none before it
    const getNearestFieldValue = (isRelatedField: (fieldName: string) => boolean) => {
      const nearestField =
        _.findLast(
          sectionFields.slice(0, Math.max(currentFieldIndex, 0)),
          (f: FieldType) => isRelatedField(f.config.fieldName),
        ) ?? sectionFields.find((f: FieldType) => isRelatedField(f.config.fieldName));
      if (!nearestField) {
        return undefined;
      }

      let nearestFieldName = nearestField.config.fieldName;
      if (field.config.fieldName.includes('.')) {
        const prefix = field.config.fieldName.slice(0, field.config.fieldName.lastIndexOf('.') + 1);
        nearestFieldName = `${prefix}${nearestFieldName}`;
      }
      return _.get(formik.values, nearestFieldName);
    };

    // selected country has priority over state, since states are filtered by the selected country
    const selectedCountry = getNearestFieldValue(checkIfIsCountryField);
    if (selectedCountry) {
      setPostalCodeCountry(selectedCountry === 'CA' ? CountryCodes.CANADA : CountryCodes.US);
      return;
    }

    setPostalCodeCountry(getPostalCodeCountryByState(getNearestFieldValue(checkIfIsStateField)));
  }, [formik.values]);

  useEffect(() => {
    if (value) {
      setIsInvalid(checkIfInvalid(value));
    }
  }, [postalCodeCountry]);

  useEffect(() => {
    if (highlightErrorFieldsState.state) {
      setIsInvalid(!!formik.getFieldMeta(field.config.fieldName).error);
    }
  }, [highlightErrorFieldsState]);

  return {
    HelperText,
    isInvalid,
    checkAndSetIsInvalid,
  };
}